import { SHARE_PATH_PREFIX } from '@itin/shared'
import { useEffect } from 'react'

/**
 * The traveller's half of the offline copy. The other half is public/sw.js.
 *
 * Nothing here is on a code path the advisor ever reaches: `useOfflineCopy` is
 * called by SharePage and by nothing else, which is the first of the two things
 * that keep the worker off her editing screens. The second is the scope below.
 */

/** Must match `STORED_AT` in public/sw.js. */
export const STORED_AT_HEADER = 'x-itin-stored-at'

/**
 * Registers the worker for the traveller's routes and no others.
 *
 * The scope is `SHARE_PATH_PREFIX` — the same constant the share URL itself is
 * built from — rather than the literal '/s/', so a worker cannot end up
 * controlling a different set of pages from the one the links point at. It is
 * NARROWER than the script's own location, which is what makes it legal without
 * a `Service-Worker-Allowed` header: a script at the root may claim any scope
 * at or below it.
 *
 * A worker at the default scope of '/' would control /trips and /trips/:id.
 * That is the defect this product shipped once already, and its symptom is Ana
 * saving an edit into a page that is several minutes old.
 */
function registerWorker(): void {
  if (!('serviceWorker' in navigator)) return

  /*
   * Not in development, and this is not laziness.
   *
   * Vite serves modules that it expects to replace on every save. A worker that
   * answers from a cache first turns that into an app which will not pick up an
   * edit — and the first thing anybody suspects is their own code, not a worker
   * they registered a week ago. The browser suite runs against the real build,
   * which is where this behaviour is exercised.
   */
  if (!import.meta.env.PROD) return

  void navigator.serviceWorker.register('/sw.js', { scope: SHARE_PATH_PREFIX }).catch(() => {
    // Locked-down profiles and some private browsing modes refuse outright. The
    // offline copy is an improvement and never a dependency: the itinerary
    // still opens, it just needs a connection, which is where this product was
    // yesterday.
  })
}

/**
 * Tells the worker which URLs this page turned out to need.
 *
 * `performance.getEntriesByType('resource')` is the browser's own record of
 * every subresource this document fetched — the hashed bundle, the stylesheet,
 * the four font files the traveller's name and dates actually pulled in, the
 * map tiles, and the API reads. Handing that list over is what makes the first
 * visit useful, because on the first visit the worker was installed after all
 * of it had already been fetched.
 *
 * It is also why there is no precache manifest and no bundler plugin: the page
 * knows the content hashes, so nothing has to generate them.
 */
async function keepWhatThisPageUsed(): Promise<void> {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return

  // Resolves once there is an ACTIVE worker whose scope covers this page. It
  // simply never settles if registration was refused, which is the right shape
  // here — there is nothing to hand the list to.
  const registration = await navigator.serviceWorker.ready
  const worker = registration.active
  if (!worker) return

  worker.postMessage({
    type: 'keep',
    urls: [
      // The document itself. It is a navigation rather than a resource, so it
      // is not in the list below.
      window.location.href,
      ...performance.getEntriesByType('resource').map((entry) => entry.name),
    ],
  })
}

/** Long enough for a burst of tiles to finish, short enough to beat a locked screen. */
const SETTLE_MS = 250

/**
 * Registers the worker and keeps its copy topped up.
 *
 * `ready` says the itinerary is on screen; passing it in rather than reading a
 * query in here keeps this file out of the data layer.
 *
 * The list is taken when the page STOPS fetching, not when it renders, and that
 * is the whole subtlety. The map's tiles cannot be requested until the plan has
 * said where the pins go, and a resource does not appear in
 * `performance.getEntriesByType` until it has finished arriving — so a list
 * taken the moment the itinerary appeared is a list with no map in it, and the
 * traveller would find a blank rectangle where the map was on the one day they
 * needed it. A `PerformanceObserver` says "more things landed"; the debounce
 * turns fifteen tiles arriving into one message rather than fifteen.
 *
 * Running more than once is free: the worker skips whatever it already holds.
 */
export function useOfflineCopy(ready: boolean): void {
  useEffect(registerWorker, [])

  useEffect(() => {
    if (!ready) return

    let timer: number | undefined
    const settle = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => void keepWhatThisPageUsed(), SETTLE_MS)
    }

    // Once for what has already landed, then again after anything else does.
    settle()
    const observer = new PerformanceObserver(settle)
    observer.observe({ type: 'resource', buffered: false })

    return () => {
      observer.disconnect()
      window.clearTimeout(timer)
    }
  }, [ready])
}
